import React from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  LabelList,
} from 'recharts';
import type { DiretoriaIndicator } from '@/data/diretoriaTypes';
import { DIRETORIA_PIE_COLORS } from '@/components/Diretoria/diretoriaColors';
import { formatCurrency, formatCurrencyCompact } from '@/lib/formatters';

const COLOR_PRIMARY = 'hsl(222 47% 18%)';
const COLOR_SECONDARY = '#ca8a04';
const COLOR_MUTED = '#64748b';

export interface DiretoriaModalChartProps {
  indicator: DiretoriaIndicator;
  height?: number;
  /** Pizza: sem rótulos no arco; legenda abaixo (recomendado para muitas fatias). */
  pieUseLegend?: boolean;
}

function seriesLabel(indicator: DiretoriaIndicator, key: 'valor' | 'valor2'): string {
  if (key === 'valor') return indicator.seriesLabels?.valor ?? 'Valor';
  return indicator.seriesLabels?.valor2 ?? 'Série B';
}

function isMoneyIndicator(indicator: DiretoriaIndicator): boolean {
  return (
    indicator.id === 'custo-moto' ||
    indicator.id === 'faturamento-bruto' ||
    indicator.id === 'rentabilidade-moto' ||
    indicator.id === 'inadimplencia' ||
    indicator.id === 'infracoes'
  );
}

function formatAxisValue(value: number, asMoney: boolean): string {
  if (asMoney) return formatCurrencyCompact(value);
  if (Math.abs(value) >= 1000) return formatCurrencyCompact(value).replace('R$\u00a0', '').replace('R$ ', '');
  return String(value);
}

function formatSeriesValue(
  indicator: DiretoriaIndicator,
  key: 'valor' | 'valor2',
  raw: number
): string {
  const moneyPrimary = new Set(['custo-moto', 'faturamento-bruto', 'rentabilidade-moto']);
  const moneySecondary = new Set(['infracoes', 'inadimplencia', 'rentabilidade-moto']);
  if (key === 'valor') {
    if (moneyPrimary.has(indicator.id)) return formatCurrency(raw);
    return String(raw);
  }
  if (indicator.id === 'custo-moto' || indicator.id === 'motos-alugadas') return String(raw);
  if (indicator.id === 'motos-status') return `${raw}%`;
  if (moneySecondary.has(indicator.id)) return formatCurrency(raw);
  return String(raw);
}

function ChartTooltipContent({
  indicator,
  active,
  payload,
}: {
  indicator: DiretoriaIndicator;
  active?: boolean;
  payload?: Array<{ payload: { name: string; valor: number; valor2?: number } }>;
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const labelValor = seriesLabel(indicator, 'valor');
  const labelValor2 = seriesLabel(indicator, 'valor2');

  return (
    <div className="rounded-md border border-border bg-background px-2.5 py-1.5 text-xs shadow-md">
      <div className="font-medium text-foreground">{row.name}</div>
      <div className="text-muted-foreground">
        {labelValor}: {formatSeriesValue(indicator, 'valor', Number(row.valor))}
      </div>
      {row.valor2 != null ? (
        <div className="text-muted-foreground">
          {labelValor2}: {formatSeriesValue(indicator, 'valor2', Number(row.valor2))}
        </div>
      ) : null}
      {indicator.id === 'infracoes' && row.valor > 0 && row.valor2 != null ? (
        <div className="text-muted-foreground">Média: {formatCurrency(row.valor2 / row.valor)}</div>
      ) : null}
    </div>
  );
}

export const DiretoriaModalChart: React.FC<DiretoriaModalChartProps> = ({
  indicator,
  height = 320,
  pieUseLegend,
}) => {
  const h = height;
  const useLegend =
    pieUseLegend ?? (indicator.chartKind === 'pie' && (indicator.pieData?.length ?? 0) >= 1);
  const data = indicator.series ?? [];
  const key = indicator.barSeriesKey ?? 'valor';
  const labelValor = seriesLabel(indicator, 'valor');
  const labelValor2 = seriesLabel(indicator, 'valor2');
  const hasValor2 = data.some((d) => d.valor2 != null);
  const moneyPrimary =
    indicator.id === 'custo-moto' ||
    indicator.id === 'faturamento-bruto' ||
    (indicator.chartKind === 'line' && indicator.id === 'rentabilidade-moto');

  if (indicator.chartKind === 'none') {
    return (
      <div
        className="flex w-full min-w-0 items-center justify-center rounded-lg border border-dashed border-border bg-muted/30 text-sm text-muted-foreground"
        style={{ height: h }}
      >
        Gráfico indisponível neste indicador — ver tabela e notas abaixo.
      </div>
    );
  }

  if (indicator.chartKind === 'pie' && indicator.pieData?.length) {
    return (
      <div style={{ height: h }} className="w-full min-h-0 min-w-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 8, right: 8, left: 8, bottom: useLegend ? 8 : 8 }}>
            <Pie
              data={indicator.pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy={useLegend ? '40%' : '50%'}
              outerRadius={useLegend ? 88 : 100}
              innerRadius={52}
              paddingAngle={1}
              label={false}
            >
              {indicator.pieData.map((_, i) => (
                <Cell key={i} fill={DIRETORIA_PIE_COLORS[i % DIRETORIA_PIE_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              formatter={(v: number, name: string) => {
                const total = indicator.pieData!.reduce((s, p) => s + p.value, 0);
                const pct = total > 0 ? ((v / total) * 100).toFixed(1) : '0';
                return [`${v} (${pct}%)`, name];
              }}
            />
            {useLegend ? (
              <Legend
                layout="horizontal"
                verticalAlign="bottom"
                wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
              />
            ) : null}
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div
        className="flex w-full min-w-0 items-center justify-center rounded-lg border border-dashed border-border bg-muted/30 text-sm text-muted-foreground"
        style={{ height: h }}
      >
        Sem série para o gráfico — ver tabela abaixo.
      </div>
    );
  }

  if (indicator.chartKind === 'barHorizontal') {
    const rowH = Math.max(28, Math.min(40, Math.floor((h - 40) / Math.max(data.length, 1))));
    const chartH = Math.max(h, data.length * rowH + 48);
    const asMoney = indicator.id === 'custo-moto';
    return (
      <div style={{ height: Math.min(chartH, h + 80), maxHeight: 420 }} className="w-full min-h-0 min-w-0 overflow-hidden">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical"
            data={data}
            margin={{ top: 8, right: asMoney ? 72 : 48, left: 8, bottom: 8 }}
          >
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" horizontal={false} />
            <XAxis
              type="number"
              tick={{ fontSize: 10 }}
              tickFormatter={(v) => formatAxisValue(Number(v), asMoney)}
            />
            <YAxis type="category" dataKey="name" width={128} tick={{ fontSize: 10 }} />
            <Tooltip content={<ChartTooltipContent indicator={indicator} />} />
            <Bar dataKey={key} fill={COLOR_PRIMARY} name={labelValor} radius={[0, 4, 4, 0]}>
              <LabelList
                dataKey={key}
                position="right"
                content={({ x, y, width, height: barH, value }) => {
                  const val = Number(value);
                  if (Number.isNaN(val)) return null;
                  const tx = Number(x) + Number(width) + 6;
                  const ty = Number(y) + Number(barH) / 2;
                  return (
                    <text
                      x={tx}
                      y={ty}
                      dominantBaseline="middle"
                      fill={COLOR_PRIMARY}
                      fontSize={10}
                      fontWeight={600}
                    >
                      {asMoney ? formatCurrencyCompact(val) : val}
                    </text>
                  );
                }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (indicator.chartKind === 'barGrouped') {
    const dualMoney = indicator.id === 'infracoes' || indicator.id === 'inadimplencia';
    return (
      <div style={{ height: h }} className="w-full min-h-0 min-w-0">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: dualMoney ? 56 : 12, left: 4, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} />
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 10 }}
              width={40}
              tickFormatter={(v) => formatAxisValue(Number(v), false)}
            />
            {hasValor2 && dualMoney ? (
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 10 }}
                width={52}
                tickFormatter={(v) => formatCurrencyCompact(Number(v))}
              />
            ) : null}
            <Tooltip content={<ChartTooltipContent indicator={indicator} />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar
              yAxisId="left"
              dataKey="valor"
              fill={COLOR_PRIMARY}
              name={labelValor}
              radius={[4, 4, 0, 0]}
            />
            {hasValor2 ? (
              <Bar
                yAxisId={dualMoney ? 'right' : 'left'}
                dataKey="valor2"
                fill={dualMoney ? COLOR_SECONDARY : COLOR_MUTED}
                name={labelValor2}
                radius={[4, 4, 0, 0]}
              />
            ) : null}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (indicator.chartKind === 'bar') {
    const longLabels = data.some((row) => (row.name?.length ?? 0) > 12) || data.length > 5;
    const asMoney = isMoneyIndicator(indicator) && indicator.id === 'custo-moto';
    return (
      <div style={{ height: h }} className="w-full min-h-0 min-w-0">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 8, right: 8, left: asMoney ? 4 : 0, bottom: longLabels ? 8 : 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 10 }}
              interval={0}
              angle={longLabels ? -35 : 0}
              textAnchor={longLabels ? 'end' : 'middle'}
              height={longLabels ? 72 : 30}
            />
            <YAxis
              tick={{ fontSize: 10 }}
              width={asMoney ? 52 : 36}
              tickFormatter={asMoney ? (v) => formatCurrencyCompact(Number(v)) : undefined}
            />
            <Tooltip content={<ChartTooltipContent indicator={indicator} />} />
            <Bar dataKey={key} fill={COLOR_PRIMARY} name={labelValor} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // line (default for chartKind === 'line')
  const asMoneyLine = moneyPrimary || indicator.id === 'faturamento-bruto';
  return (
    <div style={{ height: h }} className="w-full min-h-0 min-w-0">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
          <YAxis
            tick={{ fontSize: 10 }}
            width={asMoneyLine ? 52 : 36}
            tickFormatter={asMoneyLine ? (v) => formatCurrencyCompact(Number(v)) : undefined}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const row = payload[0].payload as { name: string; valor: number; valor2?: number };
              return (
                <div className="rounded-md border border-border bg-background px-2.5 py-1.5 text-xs shadow-md">
                  <div className="font-medium text-foreground">{row.name}</div>
                  <div className="text-muted-foreground">
                    {labelValor}: {asMoneyLine ? formatCurrency(Number(row.valor)) : row.valor}
                  </div>
                  {row.valor2 != null ? (
                    <div className="text-muted-foreground">
                      {labelValor2}:{' '}
                      {indicator.id === 'rentabilidade-moto'
                        ? formatCurrency(Number(row.valor2))
                        : row.valor2}
                    </div>
                  ) : null}
                </div>
              );
            }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Line
            type="monotone"
            dataKey="valor"
            stroke={COLOR_PRIMARY}
            strokeWidth={2}
            dot={{ r: 3 }}
            name={labelValor}
          />
          {hasValor2 ? (
            <Line
              type="monotone"
              dataKey="valor2"
              stroke={COLOR_SECONDARY}
              strokeWidth={2}
              dot={{ r: 3 }}
              name={labelValor2}
            />
          ) : null}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
